import type {
  StatementTaxonomyRowRead,
  StatementTaxonomyRowWrite,
} from "@/openapi";
import { m } from "@/paraglide/messages";

export type StatementType = "BS" | "PL";

export type DraftRow = Omit<StatementTaxonomyRowRead, "row_id"> & {
  /** Stable across renders so an unsaved row keeps focus while it is edited. */
  key: string;
  /** Null until the row is saved: an id is derived from the labels it ends up with. */
  row_id: string | null;
};

export const STATEMENT_TYPES: StatementType[] = ["BS", "PL"];

/** Gold column names derived from shared statement-tree rows. */
export const STATEMENT_TREE_COLUMNS = {
  statementType: "StatementType",
  sign: "Sign",
  level1: "Level1",
  level2: "Level2",
  level3: "Level3",
  code: "Code",
} as const;

export const STATEMENT_TREE_COLUMN_ORDER: string[] = [
  STATEMENT_TREE_COLUMNS.statementType,
  STATEMENT_TREE_COLUMNS.sign,
  STATEMENT_TREE_COLUMNS.level1,
  STATEMENT_TREE_COLUMNS.level2,
  STATEMENT_TREE_COLUMNS.level3,
];

export const statementLabel = (statementType: StatementType): string =>
  statementType === "BS"
    ? m.statement_taxonomy_editor_statement_bs()
    : m.statement_taxonomy_editor_statement_pl();

/**
 * Build the row id for a row that does not have one yet.
 *
 * Ids are only ever generated once: mapping targets are keyed on `row_id`, so
 * renaming a label later must not move the row to a different identity.
 */
export function buildRowId(
  statementType: StatementType,
  level1: string,
  level2: string,
  taken: ReadonlySet<string>,
  level3?: string | null,
): string {
  const base =
    [
      statementType.toLowerCase(),
      slug(level1),
      slug(level2),
      slug(level3 ?? ""),
    ]
      .filter(Boolean)
      .join(".") || statementType.toLowerCase();
  const id = fitRowId(base, "");
  if (!taken.has(id)) return id;
  let suffix = 2;
  while (taken.has(fitRowId(base, `_${suffix}`))) suffix += 1;
  return fitRowId(base, `_${suffix}`);
}

// the backend rejects longer ids, and long labels used to fail the whole save
const MAX_ROW_ID_LENGTH = 128;

function fitRowId(base: string, suffix: string): string {
  const head = base
    .slice(0, MAX_ROW_ID_LENGTH - suffix.length)
    .replace(/[._]+$/, "");
  return `${head}${suffix}`;
}

/**
 * Move the row keyed `fromKey` into the position currently held by `toKey`.
 *
 * The move happens in the flat list even though rows are displayed per statement:
 * only the order within a statement is read back, so repositioning across the
 * whole list keeps each statement's sequence right without regrouping it.
 */
export function reorderRows(
  rows: readonly DraftRow[],
  fromKey: string,
  toKey: string,
): DraftRow[] {
  const fromIndex = rows.findIndex((row) => row.key === fromKey);
  const toIndex = rows.findIndex((row) => row.key === toKey);
  if (fromIndex === -1 || toIndex === -1) return [...rows];

  const next = [...rows];
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return next;
}

type AssignedRow = Omit<DraftRow, "key" | "row_id"> & { row_id: string };

/**
 * Give every unsaved row an id derived from the labels it now carries.
 *
 * Generating at save rather than at add is what makes the ids meaningful: a row
 * has no labels the moment it appears, and the save is validated to reject empty
 * ones. Rows that already have an id keep it, however their labels changed.
 */
export function assignRowIds(rows: readonly DraftRow[]): AssignedRow[] {
  const taken = new Set(
    rows.map((row) => row.row_id).filter((rowId): rowId is string => !!rowId),
  );
  return rows.map(({ key: _key, ...row }) => {
    if (row.row_id) return { ...row, row_id: row.row_id };
    const rowId = buildRowId(
      row.statement_type,
      row.level_1,
      row.level_2,
      taken,
      row.level_3,
    );
    taken.add(rowId);
    return { ...row, row_id: rowId };
  });
}

/**
 * Rows as the write schema expects them: a blank level 3 is omitted so a CF
 * taxonomy stays two-level on the wire, matching omit-null dumps.
 */
export function toWriteRows(
  rows: readonly DraftRow[],
): StatementTaxonomyRowWrite[] {
  return assignRowIds(rows).map((row) => {
    const level3 = row.level_3?.trim() ?? "";
    const code = row.code?.trim() ?? "";
    // both are omitted rather than sent empty: the backend treats an absent key
    // as "this row authors none", and a blank string is not the same thing
    return {
      row_id: row.row_id,
      statement_type: row.statement_type,
      sign: row.sign,
      level_1: row.level_1,
      level_2: row.level_2,
      ...(level3 ? { level_3: level3 } : {}),
      ...(code ? { code } : {}),
    };
  });
}

/**
 * Distinct gold-column combinations the grid uses as allowed values.
 *
 * Mirrors the backend derivation: Sign is a string, and Level3 is absent when
 * the row did not author one.
 */
export function allowedValuesFromRows(
  rows: readonly Pick<
    DraftRow,
    "statement_type" | "sign" | "level_1" | "level_2" | "level_3"
  >[],
): Array<Record<string, string>> {
  const combinations: Array<Record<string, string>> = [];
  const seen = new Set<string>();
  for (const row of rows) {
    const combination: Record<string, string> = {
      [STATEMENT_TREE_COLUMNS.statementType]: String(row.statement_type),
      [STATEMENT_TREE_COLUMNS.sign]: String(row.sign),
      [STATEMENT_TREE_COLUMNS.level1]: String(row.level_1),
      [STATEMENT_TREE_COLUMNS.level2]: String(row.level_2),
    };
    const level3 = row.level_3?.trim() ?? "";
    if (level3) {
      combination[STATEMENT_TREE_COLUMNS.level3] = level3;
    }
    const key = Object.values(combination).join("\0");
    if (seen.has(key)) continue;
    seen.add(key);
    combinations.push(combination);
  }
  return combinations;
}

function labelPath(
  row: Pick<DraftRow, "level_1" | "level_2" | "level_3">,
): string[] {
  return [row.level_1, row.level_2, row.level_3 ?? ""]
    .map((label) => label.trim())
    .filter(Boolean);
}

/**
 * Keys of rows whose statement and label path another row repeats.
 *
 * Mirrors the backend check that rejects such a tree: labels are compared
 * trimmed and case-sensitive, and code and sign play no part.
 */
export function duplicateRowKeys(rows: readonly DraftRow[]): Set<string> {
  const keysByPath = new Map<string, string[]>();
  for (const row of rows) {
    if (!row.level_1.trim() || !row.level_2.trim()) continue;
    const path = [
      row.statement_type,
      row.level_1.trim(),
      row.level_2.trim(),
      row.level_3?.trim() ?? "",
    ].join("\0");
    const keys = keysByPath.get(path);
    if (keys) keys.push(row.key);
    else keysByPath.set(path, [row.key]);
  }
  return new Set(
    [...keysByPath.values()].filter((keys) => keys.length > 1).flat(),
  );
}

/** The label paths behind the server's groups of repeated rows, one per group. */
export function duplicateRowsDescription(
  rows: readonly DraftRow[],
  groups: readonly (readonly number[])[],
): string {
  return groups
    .flatMap((group) => {
      const row = group.map((index) => rows[index]).find(Boolean);
      return row ? [labelPath(row).join(" / ")] : [];
    })
    .join("; ");
}

export function toDraftRows(
  rows: readonly StatementTaxonomyRowRead[],
): DraftRow[] {
  return rows.map((row) => ({ ...row, key: row.row_id }));
}

const ISSUE_MESSAGES: Record<string, () => string> = {
  "BS:statement_empty": m.statement_taxonomy_issue_statement_empty_bs,
  "PL:statement_empty": m.statement_taxonomy_issue_statement_empty_pl,
  "BS:sign_missing": m.statement_taxonomy_issue_sign_missing_bs,
  "PL:sign_missing": m.statement_taxonomy_issue_sign_missing_pl,
  "BS:too_many_rows": m.statement_taxonomy_issue_too_many_rows_bs,
  "PL:too_many_rows": m.statement_taxonomy_issue_too_many_rows_pl,
};

/** One publish issue as it arrives, before the codes are known to be ones we name. */
type PublishIssue = {
  statement_type: string;
  failure: string;
};

/**
 * Turn the server's publish issues into messages, dropping any it cannot name.
 *
 * The codes are typed as plain strings because a server that gains a new failure
 * should leave the toast short, not render a blank line.
 */
export function publishIssueMessages(
  issues: readonly Partial<PublishIssue>[],
): string[] {
  return issues
    .map((issue) =>
      ISSUE_MESSAGES[`${issue.statement_type}:${issue.failure}`]?.(),
    )
    .filter((message): message is string => !!message);
}

function slug(value: string): string {
  // splitting on runs of non-alphanumerics avoids the anchored trim that made
  // this backtrack, and drops leading and trailing separators for free
  return value
    .normalize("NFD")
    .replaceAll(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean)
    .join("_");
}
